// ============================================
// FILTER BAR COMPONENT
// ============================================

import { ReactNode } from 'react';
import { Search, X, Filter } from 'lucide-react';
import { cn } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

interface FilterBarProps {
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  filters?: ReactNode;
  actions?: ReactNode;
  activeFiltersCount?: number;
  onClearFilters?: () => void;
  className?: string;
}

export function FilterBar({
  searchValue = '',
  onSearchChange,
  searchPlaceholder,
  filters,
  actions,
  activeFiltersCount = 0,
  onClearFilters,
  className,
}: FilterBarProps) {
  const { t } = useTranslation('common');

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-4 p-4 bg-white rounded-maritime border border-gray-200',
        className
      )}
    >
      {/* Arama */}
      {onSearchChange && (
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder || t('filters.searchDefault')}
            className="w-full h-10 pl-10 pr-4 text-sm border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500 focus:border-maritime-500"
          />
          {searchValue && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* Filtreler */}
      {filters && <div className="flex items-center gap-3">{filters}</div>}

      {/* Aktif filtre göstergesi */}
      {activeFiltersCount > 0 && (
        <button
          onClick={onClearFilters}
          className="flex items-center gap-2 px-3 py-2 text-sm text-maritime-600 hover:text-maritime-700 hover:bg-maritime-50 rounded-maritime transition-colors"
        >
          <Filter className="h-4 w-4" />
          <span>{t('filters.activeCount', { count: activeFiltersCount })}</span>
          <X className="h-4 w-4" />
        </button>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Aksiyonlar */}
      {actions && <div className="flex items-center gap-3">{actions}</div>}
    </div>
  );
}

// ============================================
// FILTER SELECT COMPONENT
// ============================================

interface FilterSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  className?: string;
}

export function FilterSelect({
  value,
  onChange,
  options,
  placeholder,
  className,
}: FilterSelectProps) {
  const { t } = useTranslation('common');

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        'h-10 px-3 text-sm border border-gray-300 rounded-maritime bg-white focus:outline-none focus:ring-2 focus:ring-maritime-500 focus:border-maritime-500',
        className
      )}
    >
      <option value="">{placeholder || t('filters.selectDefault')}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
